import { Box } from '@nimbus-ds/components';
import { InteractiveList } from '@nimbus-ds/patterns';
import { Spinner } from 'commons/components';
import { useInfiniteScroll } from 'commons/hooks';
import { SelectedProductInterface } from 'domains/Catalog/Products/components/SelectProductList/types';
import ProductListItem from './ProductListItem';
import Skeleton from './Skeleton';

interface ProductsListProps {
  products: SelectedProductInterface[];
  selectedVariantIds: string[];
  onAdd: (variantIds: string[]) => void;
  onRemove: (variantIds: string[]) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreProducts: boolean;
}

function ProductsList({
  products,
  selectedVariantIds,
  onAdd,
  onRemove,
  onLoadMore,
  isLoading,
  isLoadingMore,
  hasMoreProducts,
}: Readonly<ProductsListProps>): JSX.Element {
  const scrollTriggerRef = useInfiniteScroll(() => {
    if (hasMoreProducts && !isLoadingMore && !isLoading) {
      onLoadMore();
    }
  });

  if (isLoading) {
    return (
      <Box flex="1" overflow="auto" overflowX="hidden">
        <Skeleton />
        <div
          ref={scrollTriggerRef}
          style={{ height: '1px', visibility: 'hidden' }}
        />
      </Box>
    );
  }

  const renderProductItem = (product: SelectedProductInterface) => {
    const productVariants = product.variants.map((variant) => ({
      ...product,
      id: variant.id,
      attributes: Object.keys(variant.values).map((key) => ({
        name: key,
        values: [variant.values[key]],
      })),
    }));

    return (
      <ProductListItem
        key={product.id}
        variants={productVariants}
        selectedVariantIds={selectedVariantIds}
        onAdd={onAdd}
        onRemove={onRemove}
      />
    );
  };

  return (
    <Box flex="1" overflow="auto" overflowX="hidden">
      <InteractiveList bottomDivider={isLoadingMore && hasMoreProducts}>
        {products.map((product) => renderProductItem(product))}

        <div ref={scrollTriggerRef} style={{ height: '1px' }} />

        {isLoadingMore && hasMoreProducts && (
          <Box display="flex" justifyContent="center" padding="4">
            <Spinner show />
          </Box>
        )}
      </InteractiveList>
    </Box>
  );
}

export default ProductsList;
